import React from "react";
import PropTypes from "prop-types";
import styles from "./ImageCollage.module.scss";

// WhatsApp-style multi-image collage for chat attachments. Modeled after the
// AvatarStack `grid` layout (see ../AvatarStack) but scaled up for full-size
// photos inside a chat bubble:
//
//   1 image  →  plain image, natural aspect ratio
//   2 images →  side-by-side, wide overall ratio
//   3 images →  one large left, two stacked right
//   4 images →  2 × 2
//   5+ images → 2 × 2 with the last cell overlaying the 4th image as a
//               translucent "+N" badge so the count of hidden photos stays
//               surfaced (matches the WhatsApp / Messenger pattern).
//
// Each tile is a button so clicking a specific image opens the lightbox at
// the right index. The component is purely presentational — burst grouping,
// lightbox state, and keyboard navigation all live with the caller.
const ImageCollage = ({
  images,
  maxVisible,
  onImageClick,
  className,
  ariaLabel,
  ...rest
}) => {
  if (!Array.isArray(images) || images.length === 0) return null;

  const count = images.length;

  // Single image — skip the grid chrome entirely so a one-photo message reads
  // identically to a standalone image elsewhere in the app.
  if (count === 1) {
    const cls = [styles.single, className].filter(Boolean).join(" ");
    return (
      <button
        type="button"
        className={cls}
        onClick={() => onImageClick && onImageClick(0)}
        aria-label={ariaLabel}
        {...rest}
      >
        <img src={images[0]} alt="" draggable={false} />
      </button>
    );
  }

  // 2 → variant 2, 3 → variant 3, 4+ → variant 4. The grid template handles
  // the WhatsApp-style "1 large + 2 stacked" arrangement for variant 3 via
  // grid-row span on the first cell.
  const variant = count === 2 ? 2 : count === 3 ? 3 : 4;
  const hasOverflow = count > maxVisible;
  // We always render `maxVisible` cells when overflow is in play; the last
  // one becomes the "+N" cell and uses the (maxVisible)-th image as backdrop.
  const visibleCount = hasOverflow ? maxVisible : Math.min(count, maxVisible);
  const visible = images.slice(0, visibleCount);
  const overflow = hasOverflow ? count - maxVisible : 0;

  const cls = [
    styles.collage,
    styles[`variant${variant}`],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} role="group" aria-label={ariaLabel} {...rest}>
      {visible.map((src, idx) => {
        const isOverflowCell = hasOverflow && idx === visible.length - 1;
        return (
          <button
            type="button"
            key={idx}
            className={styles.cell}
            onClick={() => onImageClick && onImageClick(idx)}
          >
            <img src={src} alt="" draggable={false} />
            {isOverflowCell && (
              <span className={styles.overflow} aria-hidden="true">
                +{overflow}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

ImageCollage.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxVisible: PropTypes.number,
  onImageClick: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

ImageCollage.defaultProps = {
  maxVisible: 4,
  onImageClick: undefined,
  className: "",
  ariaLabel: undefined,
};

export default ImageCollage;
